/**
 * `waitForElement` function waits for an element to exist in the DOM and returns it
 * @param selector - The selector to wait for.
 * @returns {Promise<Element | null>} A promise that resolves with the element when it exists,
 * or null if the element does not exist after the timeout
 *
 * @example
 * waitForElement(".classname-to-wait-for").then((element) => {
 *   console.log(element);
 * });
 */
function waitForElement(selector: string, timeout: number = 10000): Promise<Element | null> {
  return new Promise((resolve) => {
    // Check if the element already exists
    if (window.document.body.querySelector(selector)) {
      return resolve(window.document.body.querySelector(selector));
    }

    // If the element does not exist, create a MutationObserver that will observe the DOM for changes in the body element
    // and check if the element exists
    const observer = new MutationObserver((mutations, observer) => {
      mutations.forEach(() => {
        const element = window.document.body.querySelector(selector);

        if (element) {
          observer.disconnect();
          return resolve(element);
        }
      });
    });

    // Observe the DOM for changes in the body element
    observer.observe(window.document.body, {
      childList: true,
      subtree: true,
    });

    // If the element does not exist after the timeout, return null
    setTimeout(() => {
      observer.disconnect();
      return resolve(null);
    }, timeout);
  });
}

export default waitForElement;
